import { ExternalLink } from "lucide-react"
import { site } from "./content"
// Bright-teal (#38b9ac) MT monogram, tuned for the dark footer so it pops like
// the footer's accent links (the light-teal header mark would read dim here).
import logoUrl from "@/assets/mt-logo-dark.svg"

export interface SiteFooterNavLink {
  href: string
  label: string
}

// Homepage on-page nav. Other pages (e.g. a case study) pass their own so the
// "On this page" links always point at anchors that exist on that page.
const DEFAULT_NAV: SiteFooterNavLink[] = [
  { href: "#offers", label: "How we can work" },
  { href: "#case-studies", label: "Case studies" },
  { href: "#results", label: "Results" },
]

// Dark, structured footer that grounds the page: brand + positioning line, a
// short on-page nav, and a connect column, over a bottom bar. The `dark` class
// flips this subtree to the dark palette (like the hero form island), and
// bg-page under dark is the page's darkest surface.
// `applyHref` defaults to the on-page #apply anchor. Pages that don't carry the
// lead form (the dead-link page) pass "/#apply" so the link goes home rather
// than nowhere.
export function SiteFooter({
  navHeading = "On this page",
  navLinks = DEFAULT_NAV,
  applyHref = "#apply",
}: {
  navHeading?: string
  navLinks?: SiteFooterNavLink[]
  applyHref?: string
} = {}) {
  return (
    <footer className="dark bg-page">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr]">
          <div>
            {/* MT monogram logo. Not a link here, so alt carries the brand
                name for screen readers. */}
            <img src={logoUrl} alt={site.name} className="h-9 w-auto" />
            <p className="meta mt-3 max-w-xs">
              Independent Google Ads specialist. I find wasted budget, fix it,
              and turn spend into leads that convert.
            </p>
          </div>

          <div>
            <p className="footer-heading">{navHeading}</p>
            <ul className="mt-3 flex flex-col gap-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href}>{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="footer-heading">Get in touch</p>
            <ul className="mt-3 flex flex-col gap-2">
              <li>
                <a href={applyHref} data-track-click="footer_apply">
                  Apply
                </a>
              </li>
              <li>
                <a
                  href={site.linkedinUrl}
                  data-track-click="footer_linkedin"
                  className="inline-flex items-center gap-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink aria-hidden="true" className="h-4 w-4" />
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-hairline pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="meta">© 2026 {site.name}.</p>
          <p className="meta">Google Ads that earns its budget back.</p>
        </div>
      </div>
    </footer>
  )
}
